/**
 * Analytics lists — reports GA4 select_item for product clicks in any product list.
 *
 * Two sources, one handler: <product-card> dispatches `product-card:select` after its
 * own swipe / hold / quick-ATC guards (assets/product-card.js), and every other product
 * unit is an element marked `data-list-item` whose links navigate natively (shop-the-look
 * popup, PDP upsell, favorites, recently-viewed strips). The list is the closest
 * `[data-list-id]`; identity comes from data-* attributes only, never from text or hrefs
 * (Weglot rewrites both). Same-tab clicks are deferred to the next page, modifier clicks
 * (new tab) publish right away (see @theme/analytics).
 *
 * Loaded globally from snippets/theme-script-loaders.liquid (document-level singleton).
 * Contract: GA4-EVENTS-PLAN.md sections 3 and 5.1.
 */

import { Delegate } from '@theme/delegate';
import { EVENTS, trackEvent, trackNavigationEvent } from '@theme/analytics';

const UNIT_SELECTOR = 'product-card, [data-list-item]';
const SHOP_THE_LOOK_LIST = 'shop_the_look';

// Position among the list's units, 0-based like Google's own examples. Elements that
// carry data-index (hover links copied from a slide) report that instead and are left
// out of the count so they never shift the others.
function unitIndex(unit, root) {
  if (unit.dataset.index !== undefined) return Number(unit.dataset.index);
  const units = [...root.querySelectorAll(UNIT_SELECTOR)].filter(
    (el) => el.dataset.index === undefined
  );
  const index = units.indexOf(unit);
  return index === -1 ? undefined : index;
}

function itemFromUnit(unit, root) {
  const { productId, productTitle, variantId, price, productType } = unit.dataset;
  return {
    item_id: productId,
    item_name: productTitle,
    item_variant: variantId,
    item_category: productType,
    price: price ? Number(price) : undefined,
    index: unitIndex(unit, root),
  };
}

function report(unit, trigger, newTab) {
  const root = unit.closest('[data-list-id]');
  if (!root || !unit.dataset.productId) return;

  const listId = root.dataset.listId;
  const payload = {
    item_list_id: listId,
    item_list_name: root.dataset.listName,
    items: [itemFromUnit(unit, root)],
    trigger,
  };
  let name = EVENTS.listClick;

  if (listId === SHOP_THE_LOOK_LIST) {
    name = EVENTS.shopTheLookProductClick;
    payload.look_id = root.dataset.lookId;
    payload.look_name = root.dataset.lookName;
  }

  (newTab ? trackEvent : trackNavigationEvent)(name, payload, unit);
}

document.addEventListener('product-card:select', (event) => {
  const unit = event.target.closest('product-card');
  if (unit) report(unit, event.detail?.trigger ?? 'card', Boolean(event.detail?.newTab));
});

// Native links inside data-list-item units. Cards are excluded: they report through the
// event above and swallow the clicks they cancel themselves. A click cancelled elsewhere
// (a Swiper drag) never navigates and must not count.
new Delegate(document.body).on('click', 'a[href]', (event, target) => {
  if (event.defaultPrevented || target.closest('product-card')) return;
  const unit = target.closest('[data-list-item]');
  if (!unit) return;

  const modified =
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
  report(unit, modified ? 'link_newtab' : 'link', modified);
});
